/**
 * RESPONSIBILITY: the @Video column of a @List row.
 *
 * Its own file for the same reason cell-image.tsx is: it talks to the server, and upload
 * state and upload failure belong with the thing that owns them.
 *
 * No autoplay here. A row of eight videos all playing at once is eight decoders running
 * to show an editor something they can see by pressing play — so this one has controls
 * and stays still until asked.
 */
import { useState } from 'react';
import { EMPTY_IMAGE, hasImage, type ImageValue } from '@framework/core';
import { uploadMedia } from '../upload-media';
import type { Cell } from './cell-types';

const VideoCell: Cell['render'] = ({ field, value, onChange, id }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const video: ImageValue = { ...EMPTY_IMAGE, ...(value as Partial<ImageValue> | undefined) };

  async function choose(file: File) {
    setBusy(true); setErr(null);
    const r = await uploadMedia(file, field.folder ?? 'misc', field.maxSizeMb ?? 24, 'video');
    setBusy(false);
    if (r.ok) onChange({ path: r.value.path, alt: video.alt });
    else setErr(r.error);
  }

  return (
    <div className="ctl-cell-img">
      <label className="ctl-img-pick ctl-img-sm"
             title={hasImage(video) ? 'Click to replace' : 'Click to choose'}>
        {hasImage(video)
          ? <video className="ctl-vid-preview" src={video.path} muted playsInline preload="metadata" />
          : <span className="ctl-img-empty">none</span>}
        <span className="ctl-img-veil">{busy ? '…' : hasImage(video) ? 'Replace' : 'Choose'}</span>
        <input id={id} type="file" accept="video/mp4,video/webm" disabled={busy}
               onChange={(e) => { const f = e.target.files?.[0]; if (f) void choose(f); }} />
      </label>

      {field.size && <span className="ctl-size ctl-cell-size">{field.size}</span>}

      <input
        type="text" className="ctl-input ctl-cell-alt"
        placeholder="What it shows" aria-label={`${field.label} — description`}
        value={video.alt}
        onChange={(e) => onChange({ ...video, alt: e.target.value })}
      />

      {hasImage(video) && (
        <button type="button" className="ctl-img-icon ctl-img-drop"
                onClick={() => onChange({ ...EMPTY_IMAGE })}
                title="Remove" aria-label="Remove video">✕</button>
      )}

      {err && <span className="ctl-error ctl-cell-err">{err}</span>}
    </div>
  );
};

export const videoCell: Cell = { render: VideoCell, basis: () => '26ch' };
